import camelcase from 'lodash.camelcase';

/**
 * This method should not be called directly in apps. Use either `trackNonInteractionEvent` (for above-the-fold
 * impressions that require no direct user interaction) or `trackEvent` (for every other event) instead.
 *
 * @param event -- The name GTM will check to determine how to handle the event. Either "nonInteractionDataLayerEvent" or "dataLayerEvent".
 * @param category -- Generally the name of the name of the object being tracked, e.g. Strain Tile or Dispensary Card
 * @param action -- The type of interaction, e.g. Impression or Click
 * @param label -- Often omitted, but may be used to provide additional information
 * @param customDimensions -- An object containing camel case ids relevant to the interaction, e.g. { dispensaryId: 12345, strainId: 67890 }
 */
function trackDataLayerEvent(
  event,
  category,
  action,
  label,
  customDimensions
) {
  if (typeof window === "undefined") {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  const globalDimensions = window.dataLayer.customDimensions || {};
  let dataLayerEvent = {
    event: event,
    eventCategory: category,
    eventAction: action,
    eventLabel: label
  };

  let dimensions = normalizeCustomDimensions(
    customDimensions,
    globalDimensions
  );
  window.dataLayer.push(Object.assign(dataLayerEvent, dimensions));
}

function normalizeCustomDimensions(customDimensions, globalDimensions) {
  let result = {};
  let custom = customDimensions || {};
  let global = globalDimensions || {};

  let dimensions = Object.assign(custom, global);
  Object.keys(dimensions).forEach(key => {
    result[camelcase(key)] = dimensions[key];
  });

  /**
   * GTM has a nasty of habit of grabbing values from other events. For example, if you track an event with a dispensaryId
   * in it, any subsequent events on that page will also capture that id, even if it isn't relevant to them.
   * To prevent this, we need to manually clear any values not included in global custom dimensions or in the custom
   * dimension payload for each event.
   */
  return Object.assign(
    {
      brandId: undefined,
      cartId: undefined,
      dispensaryId: undefined,
      menuItemId: undefined,
      orderId: undefined,
      productId: undefined,
      strainId: undefined,
      userId: undefined,
      merchandisingCampaignId: undefined
    },
    result
  );
}

/**
 * Use this method to send event tracking to Google Analytics (GA) via Google Tag Manager (GTM). It's configured to send
 * events the way our GTM setup expects to receive them and no additional setup is required on your part (aside from
 * ensuring GTM is loading on your page -- you can use Cephalopod to accomplish this: https://github.com/Leafly-com/cephalopod/#query-paramaters).
 *
 * Using custom dimensions:
 * We often use custom dimensions to capture ids for objects related to a click event. For example, a user might click
 * on a strain on a dispensary's menu, and we might capture a strain id, brand id, and dispensary id for that click.
 * Custom dimensions must be configured in GA and GTM, so only certain ones are supported. Check with a GTM admin via
 * the #frontend Slack channel if you need to add a new one.
 *
 * Supported custom dimensions (camel case only):
 * brandId
 * cartId
 * dealId
 * dispensaryId
 * menuItemId
 * orderId
 * productId
 * strainId
 * userId
 * merchandisingCampaignId
 *  ^ should be used exclusively for paid placements from dispensaries
 *
 * How event tracking works behind the scenes:
 * Google Tag Manager (GTM) event tracking uses a global variable called "dataLayer" to record events.
 * We push all tracked events to this variable, and our GTM admins configure GTM to send these events to Google Analytics.
 * If you need to get in touch with a GTM admin, try the #frontend Slack channel first.
 * Further reading: https://developers.google.com/tag-manager/devguide#using-a-data-layer
 *
 * @param category -- Generally the name of the name of the object being tracked, e.g. Strain Tile or Dispensary Card
 * @param action -- The type of interaction, e.g. Impression or Click
 * @param label -- Often omitted, but may be used to provide additional information
 * @param customDimensions -- An object containing camel case ids relevant to the interaction, e.g. { dispensaryId: 12345, strainId: 67890 }
 */
function trackEvent(category, action, label, customDimensions) {
  trackDataLayerEvent(
    "dataLayerEvent",
    category,
    action,
    label,
    customDimensions
  );
}

export { trackDataLayerEvent as a, trackEvent as t };
